import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './payments.module.css';
import Badge from '../../../components/Badge/Badge';
import EmptyActivity from '../../../components/Common/EmptyActivity/EmptyActivity';
import SkeletonPayout from '../../../components/Skeletons/SkeletonsPayout';
import { getPayouts } from '../../../services/associate/associateService';
import { WelletContext } from '../../../context/WelletContext';
import Money from '../../../components/Common/Money/Money';
import { formatDateSlashed } from '../../../helpers/dates';

const PaymentsList = ({ history }) => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const { t } = useTranslation();
  const welletContext = useContext(WelletContext);

  const limit = 5;

  const fetchPayouts = async () => {
    const skip = payouts.length;
    if (!hasMore || loadingMore) return;

    setLoadingMore(true);
    try {
      const result = await getPayouts(welletContext, skip, limit);
      setPayouts((prev) => [...prev, ...result]);
      setHasMore(result.length === limit);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchPayouts();
  }, []);

  const lastItemRef = useCallback(
    (node) => {
      if (loadingMore || !hasMore) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          fetchPayouts();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loadingMore, hasMore, payouts],
  );

  const handleViewDetails = (payout) => {
    history.push({
      pathname: '/payout-details',
      state: { payout },
    });
  };

  return (
    <div>
      {loading ? (
        <>
          <SkeletonPayout isFirst />
          <SkeletonPayout />
          <SkeletonPayout borderBottom={false} />
        </>
      ) : payouts.length > 0 ? (
        <>
          {payouts.map((payout, index) => {
            const isLast = index === payouts.length - 1;
            return (
              <div
                key={index}
                ref={isLast ? lastItemRef : null}
                className='row'
                onClick={() => handleViewDetails(payout)}
              >
                <div className={`col-12 pb-2 mb-2 ${styles.borderBottom}`}>
                  <div className='row'>
                    <div className='col'>
                      <div className='fw-700 mb-1'>
                        <Money value={payout.amount} /> {payout.currency}
                      </div>
                      <div className='subtitle-text'>{payout?.bankName}</div>
                    </div>
                    <div className='col-auto'>
                      <div className='mb-2 text-right' style={{ marginTop: '-4.5px' }}>
                        <Badge
                          inline
                          text={t(`badge.${payout.status}`)}
                          type={payout?.status.toLowerCase()}
                        />
                      </div>
                      <div className='fw-500 text-right font-tiny text-secondary'>
                        <div>{formatDateSlashed(payout.createdDate)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {loadingMore && (
            <>
              <SkeletonPayout />
              <SkeletonPayout borderBottom={false} />
            </>
          )}
        </>
      ) : (
        <EmptyActivity title={t('noPayments')} />
      )}
    </div>
  );
};

export default PaymentsList;
